// frontend/src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { api, channelLabel } from '../App';

const CHANNEL_COLORS = {
  ebay_source: '#6c63ff',
  ebay_store2: '#00d4aa',
  walmart: '#0078d7',
  amazon:  '#ff9900',
  poshmark: '#d4436c',
  mercari: '#ff6b6b',
};

function formatMoney(n) {
  return '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// ── Mini Bar Chart (pure SVG) ───────────────────────────────────────────────
function BarChart({ data, height = 180 }) {
  if (!data || data.length === 0) return <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No data yet</div>;
  const maxVal = Math.max(...data.map(d => d.revenue), 1);
  const barW = Math.max(20, Math.min(40, (100 / data.length)));

  return (
    <div style={{ width: '100%', height, display: 'flex', alignItems: 'flex-end', gap: 4, padding: '0 14px', boxSizing: 'border-box' }}>
      {data.map((d, i) => {
        const h = Math.max(4, (d.revenue / maxVal) * (height - 30));
        const dayLabel = new Date(d.day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });
        return (
          <div key={i} style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
              {d.revenue > 0 ? `$${Math.round(d.revenue)}` : ''}
            </div>
            <div style={{
              width: '100%', maxWidth: barW, height: h, borderRadius: '4px 4px 0 0',
              background: `linear-gradient(180deg, var(--accent) 0%, rgba(108,99,255,.5) 100%)`,
              transition: 'height .3s',
            }} title={`${d.day}: ${formatMoney(d.revenue)} (${d.orders} orders)`} />
            <div style={{ fontSize: 9, color: 'var(--muted)' }}>{dayLabel}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Donut Chart ─────────────────────────────────────────────────────────────
function DonutChart({ data, size = 170 }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.revenue, 0);
  const r = size / 2 - 18;
  const cx = size / 2, cy = size / 2;
  const strokeW = 24;
  const gap = 0.008; // gap between slices

  const colors = data.map(d => CHANNEL_COLORS[d.channel] || '#888');

  // Build arcs with gaps
  let cumulative = 0;
  const arcs = data.map((d, i) => {
    const slice = d.revenue / total;
    const start = cumulative + gap / 2;
    const end = cumulative + slice - gap / 2;
    cumulative += slice;
    return { start, end, color: colors[i], slice };
  }).filter(a => a.slice >= 0.01);

  function describeArc(startFrac, endFrac) {
    const s = (startFrac - 0.25) * 2 * Math.PI;
    const e = (endFrac - 0.25) * 2 * Math.PI;
    const large = endFrac - startFrac > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(s), y1 = cy + r * Math.sin(s);
    const x2 = cx + r * Math.cos(e), y2 = cy + r * Math.sin(e);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  }

  const totalText = total >= 10000 ? `$${(total/1000).toFixed(1)}k` : formatMoney(total);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        {/* Background ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth={strokeW} opacity=".3" />
        {/* Data arcs */}
        {arcs.map((a, i) => (
          <path key={i} d={describeArc(a.start, a.end)}
            fill="none" stroke={a.color} strokeWidth={strokeW} strokeLinecap="round" />
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" fill="var(--text)"
          fontSize={total >= 10000 ? '16' : '18'} fontWeight="800">{totalText}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--muted)" fontSize="11">30-day</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.map((d, i) => {
          const pct = total > 0 ? Math.round((d.revenue / total) * 100) : 0;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <div style={{ width: 12, height: 12, borderRadius: 4, background: colors[i], flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>{channelLabel(d.channel)}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{formatMoney(d.revenue)} ({pct}%)</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Top Products ────────────────────────────────────────────────────────────
function TopProducts({ products }) {
  if (!products || products.length === 0) return <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No sales data yet</div>;
  const maxRev = Math.max(...products.map(p => p.total_revenue), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {products.map((p, i) => {
        let img = null;
        try { img = JSON.parse(p.images || '[]')[0]; } catch {}
        const pct = (p.total_revenue / maxRev) * 100;
        return (
          <div key={p.sku} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', width: 20, textAlign: 'right' }}>
              {i + 1}
            </span>
            {img
              ? <img src={img} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
              : <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--border)', flexShrink: 0 }} />
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.title || p.sku}
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', marginTop: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`,
                  background: `linear-gradient(90deg, var(--accent2) 0%, var(--accent) 100%)`,
                  transition: 'width .5s' }} />
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent2)' }}>{formatMoney(p.total_revenue)}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.total_sold} sold</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Sales by State ──────────────────────────────────────────────────────────
function StateMap({ states }) {
  if (!states || states.length === 0) return <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No state data yet</div>;
  const top = states.slice(0, 10);
  const maxRev = Math.max(...top.map(s => s.revenue), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {top.map((s, i) => (
        <div key={s.state} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 800, color: 'var(--accent)',
            width: 28, textAlign: 'center', background: 'rgba(108,99,255,.1)', borderRadius: 4, padding: '2px 0' }}>
            {s.state}
          </span>
          <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, width: `${(s.revenue / maxRev) * 100}%`,
              background: i === 0 ? 'var(--accent2)' : i < 3 ? 'var(--accent)' : 'rgba(108,99,255,.5)',
              transition: 'width .5s' }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 55, textAlign: 'right' }}>
            {formatMoney(s.revenue)}
          </span>
          <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 30, textAlign: 'right' }}>
            {s.orders}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── KPI Card ────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, color, icon }) {
  return (
    <div className="card" style={{ padding: '18px 20px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 12, right: 14, width: 36, height: 36, borderRadius: 10,
        background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--accent2)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────────────
export default function Dashboard() {
  const [data, setData]       = useState(null);
  const [syncing, setSyncing] = useState('');
  const [showChannels, setShowChannels] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [showDark, setShowDark] = useState(false);

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data));
  }, []);

  async function triggerSync(type) {
    setSyncing(type);
    await api.post(`/sync/${type}`).catch(() => {});
    setSyncing('');
    api.get('/dashboard').then(r => setData(r.data));
  }

  if (!data) return <div className="loading">Loading...</div>;

  const { stats, sales7, salesToday, sales30, dailyRevenue, salesByChannel, topProducts, salesByState,
          listingsByChannel, recentSyncLogs, errorListings } = data;
  const darkInventory = data.darkInventory || { count: 0, ended: 0, neverListed: 0, items: [] };

  const todayVsYesterday = sales7.orders > 0 ? `${Math.round((salesToday.orders / (sales7.orders / 7)) * 100 - 100)}% vs avg` : '';

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">Dashboard</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => triggerSync('listings')} disabled={!!syncing}>
            {syncing === 'listings' ? '...' : 'Sync Listings'}
          </button>
          <button className="btn btn-ghost" onClick={() => triggerSync('orders')} disabled={!!syncing}>
            {syncing === 'orders' ? '...' : 'Sync Orders'}
          </button>
        </div>
      </div>

      {/* Today's Sales KPIs */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>
          Today's Sales
        </div>
      </div>
      <div className="dash-kpi-grid">
        <KPICard label="Today's Revenue" value={formatMoney(salesToday.revenue)}
          color="#6c63ff" icon="$" sub={todayVsYesterday} />
        <KPICard label="Today's Orders" value={salesToday.orders}
          color="#00d4aa" icon="B" />
        <KPICard label="Items Sold" value={salesToday.items}
          color="#4ade80" icon="P" />
        <KPICard label="Open Orders" value={stats.pendingOrders}
          color={stats.pendingOrders > 0 ? '#fbbf24' : '#00d4aa'} icon="!" />
      </div>

      {/* 7-day + 30-day summary */}
      <div className="dash-stats-grid">
        <div className="stat-card"><div className="stat-value">{formatMoney(sales7.revenue)}</div><div className="stat-label">7-Day Revenue</div></div>
        <div className="stat-card"><div className="stat-value">{sales7.orders}</div><div className="stat-label">7-Day Orders</div></div>
        <div className="stat-card"><div className="stat-value" style={{ fontSize: sales30.revenue >= 10000 ? 22 : 28 }}>{formatMoney(sales30.revenue)}</div><div className="stat-label">30-Day Revenue</div></div>
        <div className="stat-card"><div className="stat-value">{stats.totalProducts}</div><div className="stat-label">Active Products</div></div>
      </div>

      {/* Charts row */}
      <div className="dash-charts-grid">
        {/* Revenue chart */}
        <div className="card">
          <div className="card-title">Daily Revenue (14 days)</div>
          <BarChart data={dailyRevenue} />
        </div>

        {/* Sales by channel donut */}
        <div className="card">
          <div className="card-title">Sales by Channel (30 days)</div>
          <div style={{ padding: '10px 0' }}>
            <DonutChart data={salesByChannel} />
          </div>
        </div>
      </div>

      {/* Top products + State map */}
      <div className="dash-bottom-grid">
        <div className="card">
          <div className="card-title">Top Products (30 days)</div>
          <TopProducts products={topProducts} />
        </div>
        <div className="card">
          <div className="card-title">Sales by State (30 days)</div>
          <StateMap states={salesByState} />
        </div>
      </div>

      {/* Collapsible sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Dark Stock — in stock but not live on eBay */}
        <div className="card" style={{ padding: 0,
          border: darkInventory.count > 0 ? '1px solid rgba(255,77,109,.4)' : '1px solid var(--border)' }}>
          <div onClick={() => darkInventory.count > 0 && setShowDark(!showDark)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', cursor: darkInventory.count > 0 ? 'pointer' : 'default' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%',
                background: darkInventory.count > 0 ? 'var(--danger)' : 'var(--accent2)' }} />
              <div className="card-title" style={{ margin: 0 }}>Dark Stock
                <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--muted)', marginLeft: 8 }}>in stock, not live on eBay</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22, fontWeight: 800,
                color: darkInventory.count > 0 ? 'var(--danger)' : 'var(--accent2)' }}>{darkInventory.count}</span>
              {darkInventory.count > 0 && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.5"
                  style={{ transition: 'transform .2s', transform: showDark ? 'rotate(0)' : 'rotate(-90deg)' }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              )}
            </div>
          </div>
          {showDark && darkInventory.count > 0 && (
            <div style={{ padding: '0 18px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>
                {darkInventory.ended} ended on eBay · {darkInventory.neverListed} never listed
                {darkInventory.items.length < darkInventory.count ? ` · showing first ${darkInventory.items.length}` : ''}
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>SKU</th><th>Title</th><th>Qty</th><th>Status</th></tr></thead>
                  <tbody>
                    {darkInventory.items.map(d => (
                      <tr key={d.sku}>
                        <td className="mono">{d.sku}</td>
                        <td style={{ fontSize: 12 }}>{(d.title || '').slice(0, 48)}</td>
                        <td>{d.quantity}</td>
                        <td><span className="badge" style={{
                          background: d.reason === 'ended' ? 'rgba(255,77,109,.15)' : 'rgba(255,214,10,.15)',
                          color: d.reason === 'ended' ? 'var(--danger)' : 'var(--warn)' }}>
                          {d.reason === 'ended' ? 'Ended' : 'Never listed'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Channel Status */}
        <div className="card" style={{ padding: 0 }}>
          <div onClick={() => setShowChannels(!showChannels)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', cursor: 'pointer' }}>
            <div className="card-title" style={{ margin: 0 }}>Channel Status</div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.5"
              style={{ transition: 'transform .2s', transform: showChannels ? 'rotate(0)' : 'rotate(-90deg)' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
          {showChannels && (
            <div style={{ padding: '0 18px 14px' }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Channel</th><th>Active</th><th>Ended</th><th>Errors</th></tr></thead>
                  <tbody>
                    {['ebay_source','ebay_store2','walmart','amazon','mercari','poshmark'].map(ch => {
                      const rows = listingsByChannel.filter(r => r.channel === ch);
                      const get  = s => rows.find(r => r.status === s)?.count || 0;
                      return (
                        <tr key={ch}>
                          <td>{channelLabel(ch)}</td>
                          <td><span className="badge badge-active">{get('active')}</span></td>
                          <td><span className="badge badge-ended">{get('ended')}</span></td>
                          <td>{get('error') > 0 ? <span className="badge badge-error">{get('error')}</span> : '--'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {errorListings.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>
                    Listing Errors ({errorListings.length})
                  </div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>SKU</th><th>Channel</th><th>Error</th></tr></thead>
                      <tbody>
                        {errorListings.map(l => (
                          <tr key={l.id}>
                            <td className="mono">{l.sku}</td>
                            <td>{channelLabel(l.channel)}</td>
                            <td style={{ color: 'var(--danger)', fontSize: 12 }}>{l.error_message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Sync Activity */}
        <div className="card" style={{ padding: 0 }}>
          <div onClick={() => setShowSync(!showSync)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', cursor: 'pointer' }}>
            <div className="card-title" style={{ margin: 0 }}>Recent Sync Activity</div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.5"
              style={{ transition: 'transform .2s', transform: showSync ? 'rotate(0)' : 'rotate(-90deg)' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
          {showSync && (
            <div style={{ padding: '0 18px 14px' }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Job</th><th>Channel</th><th>Status</th><th>Records</th><th>Time</th></tr></thead>
                  <tbody>
                    {recentSyncLogs.map(l => (
                      <tr key={l.id}>
                        <td>{l.job_name}</td>
                        <td className="text-muted">{channelLabel(l.channel)}</td>
                        <td><span className={`badge ${l.status === 'success' ? 'badge-active' : l.status === 'error' ? 'badge-error' : 'badge-pending'}`}>{l.status}</span></td>
                        <td>{l.records}</td>
                        <td className="text-muted" style={{ fontSize: 12 }}>{l.started_at ? new Date(l.started_at).toLocaleTimeString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
